import { reportExpectedAuthHttpFailure } from './authHttpFailureTelemetry';
import { GatewayError } from './gateway-client/gatewayClient';

export enum RequestErrorType {
	AUTH_ERROR = 'AUTH_ERROR',
	OTHER_ERROR = 'OTHER_ERROR',
}

export enum HTTPMethod {
	GET = 'GET',
	POST = 'POST',
	PUT = 'PUT',
	PATCH = 'PATCH',
	DELETE = 'DELETE',
}

export interface RequestErrorResponse {
	errorType: RequestErrorType;
	code: number;
	errorMessage: string;
}

type AuthErrorStatusCode = 401 | 403;

const isAuthErrorStatusCode = (statusCode: number): statusCode is AuthErrorStatusCode =>
	statusCode === 401 || statusCode === 403;

type RequestOptions = {
	credentials: RequestCredentials;
	extraHeaders?: Headers;
} & (
	| {
			method: HTTPMethod.GET | HTTPMethod.DELETE;
	  }
	| { method: HTTPMethod.POST | HTTPMethod.PUT | HTTPMethod.PATCH; body?: string | FormData }
);

export async function request<T>({
	url,
	options,
}: {
	url: string;
	options: RequestOptions;
}): Promise<T> {
	const isFormData = 'body' in options && options.body instanceof FormData;

	const requestHeaders = buildHeaders({
		extraHeaders: options.extraHeaders,
		inferContentType: isFormData,
	});

	const requestOptions: RequestInit = {
		method: options.method,
		headers: requestHeaders,
		credentials: options.credentials,
		body:
			options.method === 'POST' || options.method === 'PUT' || options.method === 'PATCH'
				? options.body
				: undefined,
	};

	const response = await fetch(url, requestOptions);
	if (!response.ok) {
		const isAuthError = isAuthErrorStatusCode(response.status);

		if (isAuthError) {
			try {
				reportExpectedAuthHttpFailure({
					method: options.method,
					statusCode: response.status,
					url,
				});
			} catch {
				// Auth failure telemetry is best-effort and must not change the error returned to callers.
			}
		}

		throw new GatewayError({
			errorType: isAuthError ? RequestErrorType.AUTH_ERROR : RequestErrorType.OTHER_ERROR,
			code: response.status,
			errorMessage: `${response.status} - Failed to ${options.method} ${url}`,
		});
	}

	if (
		response.headers.get('Content-Type')?.includes('application/json') ??
		requestHeaders.get('Accept') === 'application/json'
	) {
		return response.json() as T;
	}

	return response as T;
}

function buildHeaders({
	extraHeaders,
	inferContentType,
}: {
	extraHeaders?: Headers;
	inferContentType: boolean;
}): Headers {
	const headers = new Headers();

	if (!inferContentType) {
		headers.set('Content-Type', 'application/json');
	}

	extraHeaders?.forEach((value, key) => {
		headers.set(key, value);
	});

	return headers;
}
